import { useTranslation } from "react-i18next";
import type { DashboardOverview } from "@arc/contracts";
import type { ListStatus } from "./manual-list/manual-list-model.js";

/**
 * The legacy manuals doughnut (Chart.js) as an inline SVG ring in theme tokens (ADR-033 §2.6): released = primary,
 * ready for review = attention, draft = muted. The total sits in the middle; the legend is a `<dl>` with the counts;
 * the `<figure>` carries a full text summary so the ring is never the only carrier of the numbers. With `onSelect`
 * the legend rows are buttons — the legacy's segment click that opens the Manuals page preset to that status.
 */
const R = 42;
const C = 2 * Math.PI * R;
/** The usable width inside the ring, in the SVG's own user units (r 42 minus the 12-wide stroke, with a margin). */
const INNER_WIDTH = 62;

export function ManualsDonut({ manuals, onSelect }: { manuals: DashboardOverview["manuals"]; onSelect?: (status: ListStatus) => void }) {
  const { t } = useTranslation();
  const { total, draft, review, released } = manuals;
  const segments = [
    { key: "released", label: t("status.published"), n: released, className: "stroke-success" },
    { key: "review", label: t("status.readyForReview"), n: review, className: "stroke-attention" },
    { key: "draft", label: t("status.draft"), n: draft, className: "stroke-muted-foreground/45" },
  ] as const;
  const unit = t("dashboard.manualUnit", { count: total });
  let offset = 0;
  // `.toLowerCase()` on the state name is gone: German nouns are capitalised, so lower-casing a translated word
  // produces text no German reader would write. The catalogue composes the summary instead.
  const summary =
    total === 0
      ? t("dashboard.noManualsYet")
      : t("dashboard.donutSummary", {
          total,
          breakdown: segments.map((s) => t("dashboard.countInState", { count: s.n, state: s.label })).join(", "),
        });
  return (
    <figure aria-label={summary} className="m-0 flex flex-col items-center gap-3">
      <svg viewBox="0 0 100 100" className="size-40" aria-hidden="true">
        <circle cx="50" cy="50" r={R} fill="none" className="stroke-muted" strokeWidth="12" />
        {total > 0
          ? segments.map((s) => {
              const len = (s.n / total) * C;
              const el = <circle key={s.key} cx="50" cy="50" r={R} fill="none" className={s.className} strokeWidth="12" strokeDasharray={`${String(len)} ${String(C - len)}`} strokeDashoffset={String(-offset)} transform="rotate(-90 50 50)" />;
              offset += len;
              return el;
            })
          : null}
        <text x="50" y="47" textAnchor="middle" className="fill-foreground text-[16px] font-semibold">
          {total}
        </text>
        <text
          x="50"
          y="60"
          textAnchor="middle"
          className="fill-muted-foreground text-[7px]"
          // The ring's inner width is ~62 user units, which an English "manuals" never fills and a tenant's own
          // noun easily exceeds: Takeda's "Handlungsanweisungen" spilled past the ring on both sides (live check).
          // `textLength` condenses the glyphs to fit instead, and is applied only when the label would overflow so
          // a short word is never stretched. ~3.6 units per glyph at this size, measured against the rendered SVG.
          {...(unit.length * 3.6 > INNER_WIDTH ? { textLength: INNER_WIDTH, lengthAdjust: "spacingAndGlyphs" as const } : {})}
        >
          {unit}
        </text>
      </svg>
      <figcaption className="w-full">
        <dl className="m-0 grid grid-cols-[auto_1fr_auto] items-center gap-x-2 gap-y-1 text-xs">
          {segments.map((s) => (
            <div key={s.key} className="contents">
              <span aria-hidden="true" className={`size-2.5 rounded-sm ${s.key === "released" ? "bg-success" : s.key === "review" ? "bg-attention" : "bg-muted-foreground/45"}`} />
              <dt className="text-muted-foreground">
                {onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(s.key)}
                    aria-label={t("dashboard.showManualsInState", { state: s.label })}
                    className="hover:text-foreground focus-visible:ring-ring/50 cursor-pointer rounded border-0 bg-transparent p-0 text-left text-inherit outline-none focus-visible:ring-2"
                  >
                    {s.label}
                  </button>
                ) : (
                  s.label
                )}
              </dt>
              <dd className="m-0 text-right font-medium tabular-nums">{s.n}</dd>
            </div>
          ))}
        </dl>
      </figcaption>
    </figure>
  );
}
